"use client";

import type { UserGallery, GalleryPagination } from "@/lib/api/c-photo-gallery-user";
import styles from "./CPhotoGallery.module.css";
import { ArrowIcon } from "@/components/ui/ArrowIcon/ArrowIcon";

/**
 * Prezentacyjna lista galerii usera. Stan/akcje trzyma rodzic (CPhotoGalleryView).
 */
export function GalleryList({
  galleries,
  pagination,
  page,
  loading,
  onPageChange,
  onNew,
  onEdit,
  onImages,
  onDelete,
}: {
  galleries: UserGallery[];
  pagination: GalleryPagination | null;
  page: number;
  loading: boolean;
  onPageChange: (page: number) => void;
  onNew: () => void;
  onEdit: (g: UserGallery) => void;
  onImages: (g: UserGallery) => void;
  onDelete: (g: UserGallery) => void;
}) {
  return (
    <div className={styles.list}>
      <div className={styles.listToolbar}>
        <button type="button" className={styles.btnPrimary} onClick={onNew}>
          + Dodaj galerię
        </button>
      </div>

      {loading ? (
        <div className={styles.galleryGrid} aria-hidden="true">
          {[0, 1, 2, 3].map((i) => (
            <div key={i} className={`skeleton-base ${styles.cardSkeleton}`} />
          ))}
        </div>
      ) : galleries.length === 0 ? (
        <p className={styles.empty}>Nie masz jeszcze żadnej galerii. Dodaj pierwszą!</p>
      ) : (
        <ul className={styles.galleryGrid}>
          {galleries.map((g) => (
            <li key={g.id} className={styles.card}>
              <button
                type="button"
                className={styles.cardCover}
                onClick={() => onImages(g)}
                aria-label={`Zarządzaj zdjęciami: ${g.name}`}
              >
                {g.cover ? (
                  /* eslint-disable-next-line @next/next/no-img-element */
                  <img src={g.cover.thumb_src} alt={g.name} loading="lazy" decoding="async" />
                ) : (
                  <span className={styles.cardCoverEmpty}>brak zdjęć</span>
                )}
                <span className={styles.cardCount}>{g.count_pictures}</span>
              </button>

              <div className={styles.cardBody}>
                <h3 className={styles.cardName}>{g.name}</h3>
                <span className={g.public === 1 ? styles.badgePublic : styles.badgeHidden}>
                  {g.public === 1 ? "Publiczna" : "Ukryta"}
                </span>
              </div>

              <div className={styles.cardActions}>
                {g.public === 1 && g.public_href && (
                  <a
                    href={g.public_href}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={styles.btnLink}
                    title="Zobacz galerię na stronie"
                  >
                    Zobacz
                  </a>
                )}
                <button type="button" className={styles.btnLink} onClick={() => onImages(g)}>
                  Zdjęcia
                </button>
                <button type="button" className={styles.btnLink} onClick={() => onEdit(g)}>
                  Edytuj
                </button>
                <button type="button" className={styles.btnDanger} onClick={() => onDelete(g)}>
                  Usuń
                </button>
              </div>
            </li>
          ))}
        </ul>
      )}

      {pagination && pagination.total_pages > 1 && (
        <nav className={styles.pagination} aria-label="Paginacja">
          <button
            type="button"
            className={styles.pagBtn}
            onClick={() => onPageChange(Math.max(1, page - 1))}
            disabled={page <= 1}
          >
            <ArrowIcon direction="left" />Poprzednia
          </button>
          <span className={styles.pagInfo}>
            Strona {pagination.page} z {pagination.total_pages} (łącznie {pagination.total})
          </span>
          <button
            type="button"
            className={styles.pagBtn}
            onClick={() => onPageChange(Math.min(pagination.total_pages, page + 1))}
            disabled={page >= pagination.total_pages}
          >
            Następna<ArrowIcon />
          </button>
        </nav>
      )}
    </div>
  );
}
